
import { supabase } from "@/integrations/supabase/client";
import { Flight, FlightStatus } from "@/types/flight";
import { FlightFilter, FlightCreateInput, FlightUpdateInput } from "@/types/flightService";
import { fetchUnifiedFlights } from "./unifiedFlightQuery";

export const getFlights = async (filters?: FlightFilter): Promise<Flight[]> => {
  console.log('Fetching flights with filters:', filters);
  
  try {
    // Build date range if filter provided
    let gteDate: string | undefined;
    let ltDate: string | undefined;
    
    if (filters?.date) {
      const startOfDay = new Date(filters.date);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(filters.date);
      endOfDay.setHours(23, 59, 59, 999);
      gteDate = startOfDay.toISOString();
      ltDate = endOfDay.toISOString();
    }

    let flights = await fetchUnifiedFlights({ gteDate, ltDate });
    
    // Apply client-side filters that the unified view doesn't handle
    if (filters?.organizationId) {
      flights = flights.filter(f => f.organization_id === filters.organizationId);
    }
    if (filters?.status) {
      flights = flights.filter(f => f.status === filters.status);
    }
    
    console.log(`Successfully fetched ${flights.length} unified flights`);
    return flights;
  } catch (error) {
    console.error('Failed to fetch flights:', error);
    throw error;
  }
};

export const getFlightById = async (id: string): Promise<Flight> => {
  console.log('Fetching flight by id:', id);
  
  try {
    const { data, error } = await supabase
      .from('flights')
      .select(`
        *,
        organization:organizations(*),
        departure_city:cities!departure_city_id(*),
        arrival_city:cities!arrival_city_id(*),
        aircraft_type:aircraft_types(*)
      `)
      .eq('id', id)
      .single();
    
    if (error) {
      console.error('Error fetching flight:', error);
      throw error;
    }
    
    console.log('Successfully fetched flight:', data);
    return data as unknown as Flight;
  } catch (error) {
    console.error('Failed to fetch flight:', error);
    throw error;
  }
};

export const createFlight = async (input: FlightCreateInput) => {
  console.log('Creating flight:', input);
  
  try {
    const { data, error } = await supabase
      .from('flights')
      .insert({
        flight_number: input.flight_number,
        organization_id: input.organization_id,
        departure_city_id: input.departure_city_id,
        arrival_city_id: input.arrival_city_id,
        aircraft_type_id: input.aircraft_type_id,
        scheduled_departure: input.scheduled_departure.toISOString(),
        scheduled_arrival: input.scheduled_arrival.toISOString(),
        status: input.status,
        status_reason: input.status_reason,
        gate: input.gate,
        created_by: input.created_by,
        updated_by: input.updated_by
      })
      .select();
    
    if (error) {
      console.error('Error creating flight:', error);
      throw error;
    }
    
    console.log('Successfully created flight:', data[0]);
    return data[0];
  } catch (error) {
    console.error('Failed to create flight:', error);
    throw error;
  }
};

export const updateFlight = async (id: string, input: FlightUpdateInput) => {
  console.log('Updating flight:', id, input);
  
  try {
    const updates: Record<string, any> = {};
    
    if (input.flight_number) updates.flight_number = input.flight_number;
    if (input.departure_city_id) updates.departure_city_id = input.departure_city_id;
    if (input.arrival_city_id) updates.arrival_city_id = input.arrival_city_id;
    if (input.aircraft_type_id) updates.aircraft_type_id = input.aircraft_type_id;
    if (input.scheduled_departure) updates.scheduled_departure = input.scheduled_departure.toISOString();
    if (input.scheduled_arrival) updates.scheduled_arrival = input.scheduled_arrival.toISOString();
    if (input.status) updates.status = input.status;
    if (input.status_reason !== undefined) updates.status_reason = input.status_reason;
    if (input.gate !== undefined) updates.gate = input.gate;
    
    const { data, error } = await supabase
      .from('flights')
      .update(updates)
      .eq('id', id)
      .select();
    
    if (error) {
      console.error('Error updating flight:', error);
      throw error;
    }
    
    console.log('Successfully updated flight:', data[0]);
    return data[0];
  } catch (error) {
    console.error('Failed to update flight:', error);
    throw error;
  }
};

export const deleteFlight = async (id: string) => {
  console.log('Deleting flight:', id);
  
  try {
    const { error } = await supabase
      .from('flights')
      .delete()
      .eq('id', id);
    
    if (error) {
      console.error('Error deleting flight:', error);
      throw error;
    }
    
    console.log('Successfully deleted flight:', id);
    return true;
  } catch (error) {
    console.error('Failed to delete flight:', error);
    throw error;
  }
};
